import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { getLastResults, getDetailResults } from "../../../apiservices/CasionApi";
import RefResultModal from "../../casinoComponents/refResultModal";
import Joker20ResultBody from "../../casinoComponents/refResultModal/Joker20ResultBody";
import Joker1ResultBody from "../../casinoComponents/refResultModal/Joker1ResultBody";
import { formatRefMatchTime } from "../../../utils/refMatchTime";

/**
 * Last Result items for Teenpatti Joker (teenjoker) and Unlimited Joker Oneday / 20-20 (joker1, joker120),
 * reference LastResultsSection markup (rendered inside .casino-video-last-results):
 *   teenjoker        → span.resulta "A" / span.resultb "B" (same branch as joker20)
 *   joker1, joker120 → span.resulttie "R" for every round
 * Click → .loader-overlay while the round detail loads, then the reference result modal ("{ename} Result").
 * `onTitle` receives the feed's res1.cname (the reference game-header name).
 */
export default function TeenJokerRefResults({ gameType, fallbackTitle, onTitle }) {
  const apiType = String(gameType || "").toLowerCase().trim();
  const isJoker1 = apiType === "joker1" || apiType === "joker120";
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults(apiType);
        const res = response?.data?.data?.res;
        if (Array.isArray(res)) setLastResults(res);
        const cname = response?.data?.data?.res1?.cname;
        if (cname && onTitle) onTitle(cname);
      } catch (e) {
        console.error("Error fetching last results:", e);
      }
    };
    fetchLastResults();
    const interval = setInterval(fetchLastResults, 5000);
    return () => clearInterval(interval);
  }, [apiType, onTitle]);

  const handleResultClick = async (mid) => {
    if (loading || !mid) return;
    setLoading(true);
    try {
      const response = await getDetailResults(apiType, String(mid));
      const t1 = response?.data?.data?.t1 ?? response?.data?.t1;
      if (t1) {
        setDetailResult(t1);
        setShowModal(true);
      }
    } catch (e) {
      console.error("Error fetching detail results:", e);
    }
    setLoading(false);
  };

  return (
    <>
      {lastResults.map((r, i) => {
        if (isJoker1) {
          return (
            <span key={`${r?.mid}-${i}`} className="resulttie" onClick={() => handleResultClick(r?.mid)}>
              R
            </span>
          );
        }
        const w = String(r?.win ?? "");
        return (
          <span
            key={`${r?.mid}-${i}`}
            className={w === "1" ? "resulta" : w === "2" ? "resultb" : undefined}
            onClick={() => handleResultClick(r?.mid)}
          >
            {w === "1" ? "A" : null} {w === "2" ? "B" : null} {w === "0" ? "T" : null}
          </span>
        );
      })}
      {loading && createPortal(<div className="loader-overlay" />, document.querySelector(".page-content") || document.body)}
      <RefResultModal
        show={showModal && !!detailResult}
        onHide={() => setShowModal(false)}
        title={`${detailResult?.ename || fallbackTitle} Result`}
        roundId={detailResult?.rid}
        matchTime={formatRefMatchTime(detailResult?.mtime)}
      >
        {detailResult &&
          (isJoker1 ? <Joker1ResultBody t1={detailResult} /> : <Joker20ResultBody t1={detailResult} />)}
      </RefResultModal>
    </>
  );
}
